import { useReducer, useRef, createContext, useContext, ReactElement, useEffect } from 'react';

import { useRouter } from 'next/router';

import { useTenancy } from '@utmb/contexts/TenancyContext';

import SearchMagResultView from '@utmb/types/view/SearchMagResultView';
import {
    ActionKind,
    AddMoreMagsAction,
    FetchMagsAction,
    FilterOnThemeAction,
    FilterOnTypeAction,
    initialState,
    LoadMoreMagsAction,
    reducer,
    RestoreFiltersAction,
    SetMagsAction,
    SetSearchAction,
} from '@utmb/reducers/mags';
import { MagsContextProps, MagsProviderProps, SearchMagDto, StoredFilters } from '@utmb/types/mags';
import { fetchClientAPI } from '@utmb/utils/utmb';
import { buildQuery } from '@utmb/utils/query';

const MagsContext = createContext({} as MagsContextProps);

const fetchClientMags = async (tenantId: string, searchMagDto: SearchMagDto): Promise<SearchMagResultView> => {
    const path = buildQuery(`/search/mag`, searchMagDto);

    return await fetchClientAPI(tenantId, path);
};

export const MagsProvider = ({ result: { mags, nbHits, limit }, filters, children }: MagsProviderProps): ReactElement => {
    const { tenantId } = useTenancy();
    const { locale } = useRouter();

    const localStorageFilterKey = 'utmb-news';

    const [state, dispatch] = useReducer(reducer, { ...initialState, mags, nbHits, filters, limit });

    const { search, offset, selectedType, selectedThemes } = state;
    const previousFilters = useRef({ search, selectedType, selectedThemes }).current;

    useEffect(() => {
        const storedFilter = sessionStorage.getItem(localStorageFilterKey);

        if (storedFilter) {
            dispatch({ type: ActionKind.RESTORE_FILTERS, payload: JSON.parse(storedFilter) as StoredFilters } as RestoreFiltersAction);
        }
    }, []);

    useEffect(() => {
        if (
            search !== previousFilters.search ||
            selectedType !== previousFilters.selectedType ||
            selectedThemes.length !== previousFilters.selectedThemes.length
        ) {
            fetchMags();
        }

        saveFilter();

        return () => {
            previousFilters.search = search;
            previousFilters.selectedType = selectedType;
            previousFilters.selectedThemes = selectedThemes;
        };
    }, [search, selectedType, selectedThemes]);

    const saveFilter = (): void => {
        sessionStorage.setItem(localStorageFilterKey, JSON.stringify({ search, selectedType, selectedThemes } as StoredFilters));
    };

    const fetchMags = async (): Promise<void> => {
        dispatch({ type: ActionKind.FETCH_MAGS } as FetchMagsAction);

        const { mags, nbHits } = await fetchClientMags(tenantId, {
            lang: locale,
            search,
            type: selectedType,
            themes: selectedThemes,
            offset: 0,
            limit,
        });

        dispatch({
            type: ActionKind.SET_MAGS,
            payload: {
                mags,
                nbHits,
            },
        } as SetMagsAction);
    };

    const loadMoreMags = async (): Promise<void> => {
        dispatch({ type: ActionKind.LOAD_MODE_MAGS } as LoadMoreMagsAction);

        const { mags } = await fetchClientMags(tenantId, {
            lang: locale,
            search,
            type: selectedType,
            themes: selectedThemes,
            offset: offset + limit,
            limit,
        });

        dispatch({ type: ActionKind.ADD_MORE_MAGS, payload: { mags, offset: offset + limit } } as AddMoreMagsAction);
    };

    const handleCheckType = (value: string): void => {
        dispatch({ type: ActionKind.FILTER_ON_TYPE, payload: value } as FilterOnTypeAction);
    };

    const handleCheckTheme = (value: string): void => {
        dispatch({ type: ActionKind.FILTER_ON_THEME, payload: value } as FilterOnThemeAction);
    };

    const handleChangeSearch = (search: string): void => {
        dispatch({ type: ActionKind.SET_SEARCH, payload: search } as SetSearchAction);
    };

    return (
        <MagsContext.Provider
            value={{
                ...state,
                loadMoreMags,
                handleCheckType,
                handleCheckTheme,
                handleChangeSearch,
            }}
        >
            {children}
        </MagsContext.Provider>
    );
};

export const useMags = () => {
    const context = useContext(MagsContext);
    if (context === undefined) {
        throw new Error(`useMags must be used within an MagsProvider`);
    }
    return context;
};
